import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  doc,
  updateDoc,
  writeBatch,
  type Timestamp,
} from "firebase/firestore";
import { getFirebaseFirestore } from "@/lib/firebase/config";

export type AppNotification = {
  id: string;
  title: string;
  body: string;
  type: string;
  data: Record<string, string>;
  read: boolean;
  createdAt: Timestamp | null;
};

export function useNotifications(adminId: string | undefined) {
  const scope = adminId;
  const [feed, setFeed] = useState<{ scope: typeof scope; items: AppNotification[] }>({ scope, items: [] });
  const notifications = useMemo(() => feed.scope === scope ? feed.items : [], [feed, scope]);


  useEffect(() => {
    if (!adminId) return;
    const db = getFirebaseFirestore();
    if (!db) return;

    const q = query(
      collection(db, "notifications"),
      where("ownerId", "==", adminId),
      where("ownerType", "==", "ADMIN"),
      orderBy("createdAt", "desc"),
      limit(50),
    );

    let active = true;
    const unsub = onSnapshot(
      q,
      (snap) => {
        if (!active) return;
        setFeed({ scope, items:
          snap.docs.map((d) => ({
            id: d.id,
            ...(d.data() as Omit<AppNotification, "id">),
          })),
        });
      },
      (error) => {
        console.warn("Notification listener error", error);
      },
    );

    return () => { active = false; unsub(); };
  }, [adminId, scope]);

  // Kept in sync via an effect so the stable `markAllAsRead` callback can
  // read the latest snapshot without re-creating itself on every update.
  const notificationsRef = useRef(notifications);
  useEffect(() => { notificationsRef.current = notifications; }, [notifications]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const markAsRead = useCallback(async (notificationId: string) => {
    const db = getFirebaseFirestore();
    if (!db) return;
    try {
      await updateDoc(doc(db, "notifications", notificationId), { read: true });
    } catch (error) {
      console.warn("Failed to mark notification as read", error);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    const db = getFirebaseFirestore();
    if (!db) return;
    const unread = notificationsRef.current.filter((n) => !n.read);
    if (unread.length === 0) return;
    try {
      // Firestore batches are limited to 500 operations
      const BATCH_LIMIT = 500;
      for (let i = 0; i < unread.length; i += BATCH_LIMIT) {
        const chunk = unread.slice(i, i + BATCH_LIMIT);
        const batch = writeBatch(db);
        for (const n of chunk) {
          batch.update(doc(db, "notifications", n.id), { read: true });
        }
        await batch.commit();
      }
    } catch (error) {
      console.warn("Failed to mark all notifications as read", error);
    }
  }, []);

  return { notifications, unreadCount, markAsRead, markAllAsRead };
}
